import type { BookerSuggestions } from '../../../api/comms';
import type { CrmContact } from '../../../api/types';
import { forgetLatestBooking } from '../gmail/latestBookings';
import type { CommsSource } from '../source';

/**
 * The three calls behind "who is this?" — suggest, link a customer, remember a
 * desk — as the booking box and the contact panel both use them.
 *
 * Wrapped rather than handed over as `source.api` so a link also drops what the
 * mail list knows about the contact's last reservation: that chip is cached
 * for a minute, and the agent who just fixed the link is looking straight at
 * it. Build it once per source (useMemo) — the box keys callbacks on it.
 */
export interface BookerLinks {
  suggest: (contactId: number, q?: string) => Promise<BookerSuggestions>;
  linkCustomer: (
    contactId: number,
    dispatchCustomerId: number | null,
    opts?: { clearAffiliate?: boolean },
  ) => Promise<CrmContact>;
  linkAffiliate: (contactId: number, dispatchAffiliateId: number | null) => Promise<CrmContact>;
}

export const bookerLinksFor = (source: CommsSource): BookerLinks => ({
  suggest: (contactId, q) => source.api.getBookerSuggestions(contactId, q),
  linkCustomer: async (contactId, dispatchCustomerId, opts) => {
    const contact = await source.api.linkDispatchCustomer(contactId, dispatchCustomerId, opts);
    forgetLatestBooking(source, contactId);
    return contact;
  },
  // A desk's bookings are this contact's too, so the chip is just as stale.
  linkAffiliate: async (contactId, dispatchAffiliateId) => {
    const contact = await source.api.linkDispatchAffiliate(contactId, dispatchAffiliateId);
    forgetLatestBooking(source, contactId);
    return contact;
  },
});
